import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import ConversionsController from 'cio/controllers/conversions';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import NewsletterModel from 'cio/models/newsletter';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';
import { Channel, Measure } from 'cio/utils/metrics/common';
import { Filters } from 'cio/utils/filters';
import { showConversionEventErrors } from 'cio/utils/conversions';

const conversionActionMeasures: Record<string, Measure> = {
  receiving: 'delivered',
  opening: 'opened',
  clicking: 'clicked',
};

export default class IndexController extends ConversionsController {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;

  declare model: NewsletterModel;

  get availableConversionActions() {
    let { conversionActions } = this;

    let filteredConversionActions = conversionActions.filter((action) => {
      return isDeliveryMeasureEnabledForChannel(conversionActionMeasures[action.value], this.model.channel as Channel);
    });

    if (this.model.channel === 'webhook') {
      return filteredConversionActions.filter((item: { value: string }) => {
        return item.value !== 'opening';
      });
    }

    return filteredConversionActions;
  }

  @computed('model.validConversion', 'eventConversionCondition.hasErrors')
  get isInvalid() {
    let eventConditionHasErrors = this.eventConversionCondition?.hasErrors ?? false;
    return showConversionEventErrors(this.model, eventConditionHasErrors);
  }

  @computed('model.{hasDirtyAttributes,conversionWindow}', 'conversionWindow', 'eventConversionCondition.isDirty')
  get isDirty() {
    return this.model.hasDirtyAttributes || (this.eventConversionCondition?.isDirty ?? false);
  }

  saveNewsletterTask = task(async () => {
    this.model.set('updateType', 'tracking');

    if (this.model.conversionEventName != null) {
      this.model.set('conversionEventName', this.model.conversionEventName.trim());
    }

    let res = await this.model.save();
    this.resetConversionSnapshot();

    // This is a hack to reset the `isDirty` state on the group condition and its children
    this.createGroupCondition(this.model.conversionEventFilters);

    return res;
  });

  resetTask = task(async () => {
    await this.model.rollbackAttributes();
    this.createGroupCondition(this.model.conversionEventFilters);
    this.analytics.track('broadcast', 'edit_discarded', { model: this.model, from: 'tracking' });
  });

  @action
  updateEventConversionFilters(serializedFilters: Filters) {
    this.model.set('conversionEventFilters', serializedFilters);
  }
}
